import React, { useState } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import {
    Box,
    CssBaseline,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography,
    AppBar,      // 🚀 Aggiunto per la barra superiore
    Toolbar,     // 🚀 Aggiunto per contenere l'header
    IconButton,  // 🚀 Aggiunto per il pulsante mobile
    Drawer,      // 🚀 Sostituisce la tua Sidebar fissa
    useTheme,
    useMediaQuery,
} from '@mui/material';

// Importa l'immagine del logo
import Logo from '../assets/logo.png'; 

// Icone MUI
import PeopleIcon from "@mui/icons-material/People";
import EventIcon from "@mui/icons-material/Event";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import BarChartIcon from "@mui/icons-material/BarChart";
import MenuIcon from '@mui/icons-material/Menu'; // 🚀 Icona per il menu mobile

// Larghezza fissa della sidebar
const drawerWidth = 240;

// Colori del tema
const SIDEBAR_BG_COLOR = '#f5cdfaff'; // Il tuo rosa chiaro
const TEXT_INACTIVE = '#4e4e4eff';   // Il tuo grigio
const LINK_ACTIVE = '#A30000';      // Rosso admin per contrasto
const ICON_ACTIVE = '#A30000';
const ICON_INACTIVE = '#4e4e4eff';

export default function MainLayout() {
    const [mobileOpen, setMobileOpen] = useState(false);
    const theme = useTheme();
    // Controlla se siamo su uno schermo piccolo (mobile/tablet)
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    // --- Stili per NavLink ---
    // Questi stili vengono applicati dinamicamente da NavLink
    const getNavLinkStyle = ({ isActive }) => ({
        color: isActive ? LINK_ACTIVE : TEXT_INACTIVE,
        backgroundColor: isActive ? 'rgba(0, 0, 0, 0.08)' : 'transparent',
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
        }
    });

    const getIconStyle = (isActive) => ({
        color: isActive ? ICON_ACTIVE : ICON_INACTIVE,
    });
    

    // Contenuto della Sidebar (spostato in una variabile per riutilizzarlo)
    const drawerContent = (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
            <Box>
                {/* Sezione Logo e Nome Palestra */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        mb: 4,
                        textAlign: 'center',
                        padding: theme.spacing(3),
                    }}
                >
                    {/* Logo (usando Box component per coerenza MUI) */}
                    <Box
                        component="img"
                        src={Logo}
                        alt="Soulfit Logo"
                        sx={{ width: 150, height: 100, marginBottom: 1 }}
                    />

                    {/* Nome della palestra */}
                    <Typography
                        variant="subtitle1"
                        sx={{
                            textAlign: 'center',
                            color: TEXT_INACTIVE, // Testo grigio
                            fontWeight: 'bold',
                            lineHeight: 1.2
                        }}
                    >
                        Soulfit Admin
                    </Typography>
                </Box>

                <List sx={{ padding: 0 }}>
                    {/* Dashboard */}
                    <ListItem button component={NavLink} to="/" end sx={getNavLinkStyle}>
                        <ListItemIcon><BarChartIcon sx={theme => getIconStyle(theme.isActive)} /></ListItemIcon>
                        <ListItemText primary="Dashboard" />
                    </ListItem>

                    {/* Gestione Utenti */}
                    <ListItem button component={NavLink} to="/users" sx={getNavLinkStyle}>
                        <ListItemIcon><PeopleIcon sx={theme => getIconStyle(theme.isActive)} /></ListItemIcon>
                        <ListItemText primary="Gestione Utenti" />
                    </ListItem>

                    {/* Prenotazioni */}
                    <ListItem button component={NavLink} to="/bookings" sx={getNavLinkStyle}>
                        <ListItemIcon><EventIcon sx={theme => getIconStyle(theme.isActive)} /></ListItemIcon>
                        <ListItemText primary="Prenotazioni giornaliere" />
                    </ListItem>

                    {/* Certificati */}
                    <ListItem button component={NavLink} to="/certificates" sx={getNavLinkStyle}>
                        <ListItemIcon><AssignmentIcon sx={theme => getIconStyle(theme.isActive)} /></ListItemIcon>
                        <ListItemText primary="Certificati" />
                    </ListItem>

                    {/* Schedulazione attività */}
                    <ListItem button component={NavLink} to="/activities" sx={getNavLinkStyle}>
                        <ListItemIcon><ScheduleIcon sx={theme => getIconStyle(theme.isActive)} /></ListItemIcon>
                        <ListItemText primary="Schedulazione attività" />
                    </ListItem>

                    {/* Giorni di Chiusura */}
                    <ListItem button component={NavLink} to="/closure" sx={getNavLinkStyle}>
                        <ListItemIcon><CalendarTodayIcon sx={theme => getIconStyle(theme.isActive)} /></ListItemIcon>
                        <ListItemText primary="Giorni di Chiusura / Annunci" />
                    </ListItem>
                </List>
            </Box>
            <Typography variant="caption" sx={{ textAlign: "center", p: 2, color: TEXT_INACTIVE }}>
                V. 1.0.17
            </Typography>
        </Box>
    );

    return (
        <Box sx={{ display: "flex", minHeight: "100vh", backgroundColor: "#f8f8f8" }}>
            <CssBaseline />

            {/* 🚀 AppBar (Barra Superiore) */}
            <AppBar
                position="fixed"
                sx={{
                    // Su desktop, la larghezza è ridotta e spostata a destra
                    width: { md: `calc(100% - ${drawerWidth}px)` },
                    ml: { md: `${drawerWidth}px` },
                    backgroundColor: '#ffffff', // Sfondo bianco pulito
                    color: TEXT_INACTIVE, // Testo scuro
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)', // Ombra leggera
                }}
            >
                <Toolbar>
                    {/* Pulsante Menu (appare solo su mobile) */}
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { md: 'none' } }} // Nascondi su desktop
                    >
                        <MenuIcon />
                    </IconButton>
                    <Typography variant="h6" noWrap component="div">
                        Pannello di Controllo
                    </Typography>
                </Toolbar>
            </AppBar>

            {/* 🚀 Drawer (Sidebar) */}
            <Box
                component="nav"
                sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
                aria-label="mailbox folders"
            >
                {/* Versione Mobile (Temporanea, a comparsa) */}
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true, // Migliore performance su mobile
                    }}
                    sx={{
                        display: { xs: 'block', md: 'none' }, // Mostra solo su mobile
                        '& .MuiDrawer-paper': { 
                            boxSizing: 'border-box', 
                            width: drawerWidth, 
                            backgroundColor: SIDEBAR_BG_COLOR,
                        },
                    }}
                >
                    {drawerContent}
                </Drawer>

                {/* Versione Desktop (Permanente, fissa) */}
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'none', md: 'block' }, // Mostra solo su desktop
                        '& .MuiDrawer-paper': { 
                            boxSizing: 'border-box', 
                            width: drawerWidth, 
                            backgroundColor: SIDEBAR_BG_COLOR,
                            borderRight: 'none', // Rimuove il bordo
                        },
                    }}
                    open
                >
                    {drawerContent}
                </Drawer>
            </Box>

            {/* Contenuto Principale (dove appaiono le pagine) */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    width: { md: `calc(100% - ${drawerWidth}px)` }
                }}
            >
                {/* 🚀 Spacer Obbligatorio: spinge il contenuto sotto l'AppBar */}
                <Toolbar /> 
                
                <Outlet />
            </Box>
        </Box>
    );
}